/**
 * @cfleet/auth - Auth Adapter Types
 *
 * Defines the interface for authentication adapters.
 * Adapters abstract the underlying auth mechanism (local JWT vs Better Auth)
 * allowing seamless switching between providers.
 */

import type { User, AuthTokens, Organization, LoginCredentials, AppAuthConfig } from '../types';

/**
 * Authoritative "unauthenticated" error.
 *
 * Thrown only when the server explicitly declares the session invalid
 * (HTTP 401). Network failures, timeouts, and 5xx responses must NOT use this —
 * they are "can't confirm" conditions that should degrade to retry-later, not
 * logout. Callers (e.g. hydration's background reconcile) clear stored
 * credentials only when they catch this specific error.
 */
export class AuthUnauthorizedError extends Error {
  constructor(message = 'Session is no longer valid') {
    super(message);
    this.name = 'AuthUnauthorizedError';
  }
}

/**
 * Response from authentication operations (login, signup, refresh)
 */
export interface AuthAdapterResponse {
  /** Normalized user object */
  user: User;
  /** Authentication tokens (may be empty for cookie-based auth) */
  tokens: AuthTokens;
  /** User's organizations for multi-tenant support */
  organizations: Organization[];
}

/**
 * Session response from Better Auth get-session endpoint
 */
export interface SessionResponse {
  /** Session information */
  session: {
    id: string;
    userId: string;
    expiresAt: string;
    createdAt: string;
    updatedAt: string;
  };
  /** User information from session */
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    image?: string;
    createdAt: string;
    updatedAt: string;
    /** Custom CFleet fields */
    role?: string;
    identity_type?: string;
    organization_ids?: string;
    device_id?: string;
    /** Authorization fields (matching core backend) */
    status?: string;
    permissions?: string;
    cfleet_user_id?: string;
    cfleet_driver_id?: string;
    cfleet_customer_id?: string;
  };
}

/**
 * Better Auth sign-up response
 */
export interface BetterAuthSignUpResponse {
  token?: string;
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    image?: string;
    createdAt: string;
    updatedAt: string;
    role?: string;
    identity_type?: string;
    organization_ids?: string;
    /** Authorization fields */
    status?: string;
    permissions?: string;
  };
}

/**
 * Better Auth sign-in response
 */
export interface BetterAuthSignInResponse {
  token?: string;
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    image?: string;
    createdAt: string;
    updatedAt: string;
    role?: string;
    identity_type?: string;
    organization_ids?: string;
    /** Authorization fields */
    status?: string;
    permissions?: string;
  };
  session?: {
    id: string;
    expiresAt: string;
  };
}

/**
 * Auth adapter interface
 *
 * Implementations provide authentication operations for different backends.
 * - LocalAuthAdapter: Direct API calls to cfleet-core-server
 * - BetterAuthAdapter: Cookie-based auth via Better Auth sidecar
 */
export interface AuthAdapter {
  /**
   * Authenticate with email/password credentials
   * @param credentials - Login credentials
   * @returns Authentication response with user and tokens
   */
  login(credentials: LoginCredentials): Promise<AuthAdapterResponse>;

  /**
   * Sign out and invalidate session
   * @param accessToken - Current access token (for local auth)
   */
  logout(accessToken: string | null): Promise<void>;

  /**
   * Refresh the current session
   * @param refreshToken - Current refresh token (for local auth)
   * @returns Updated authentication response
   */
  refreshSession(refreshToken: string | null): Promise<AuthAdapterResponse>;

  /**
   * Get current session (for cookie-based auth)
   * Returns null if no valid session exists
   */
  getSession(): Promise<SessionResponse | null>;

  /**
   * Register a new user account
   * @param email - User email
   * @param password - User password
   * @param name - User display name
   * @param additionalData - Optional additional registration data
   */
  signUp?(
    email: string,
    password: string,
    name: string,
    additionalData?: Record<string, unknown>
  ): Promise<AuthAdapterResponse>;

  /**
   * Request password reset email
   * @param email - User email
   */
  forgotPassword?(email: string): Promise<void>;

  /**
   * Reset password with token
   * @param token - Password reset token
   * @param newPassword - New password
   */
  resetPassword?(token: string, newPassword: string): Promise<void>;

  /**
   * Whether this adapter uses cookie-based sessions
   * When true, tokens may be empty and session is managed via cookies
   */
  readonly usesCookies: boolean;

  /**
   * Adapter name for debugging/logging
   */
  readonly name: string;
}

/**
 * Configuration for creating an auth adapter
 */
export interface AuthAdapterConfig {
  /** Base URL for API requests */
  baseUrl: string;
  /** App-specific auth configuration */
  appConfig: AppAuthConfig;
}
